import { CoherenceCheck } from '@coherence-design-system/controls';
import {
  CheckboxVisibility,
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  Dialog,
  DialogType,
  Dropdown,
  IColumn,
  ICommandBarItemProps,
  IDetailsListCheckboxProps,
  IDetailsRowProps,
  IDropdownOption,
  Icon,
  Label,
  Link,
  MarqueeSelection,
  PivotItem,
  ProgressIndicator,
  SearchBox,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { useUser } from '@micro-frontend-react/employee-experience/lib/useUser';
import * as React from 'react';
import { Context as ReactContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { v4 as uuidv4 } from 'uuid';

import { AttachmentItem, DeleteAttachmentRequest } from '../../../Common/CommonModels/Attachments';
import * as Constants from '../../../Common/CommonModels/Constants';
import * as PopupText from '../../../Common/CommonModels/PopupText';
import { CDMRecord, withDefaultIngestionMethod } from '../../../Common/DataOwnerModels/CDMRecord';
import {
  DataOwnerRequestContext,
  IDataOwnerRequestContext,
  IDataOwnerRequestDetails,
} from '../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { DataOwnerRequestRecord } from '../../../Common/DataOwnerModels/DataOwnerRequestRecord';
import { RuleSetRecord } from '../../../Common/DataOwnerModels/RuleSetRecord';
import * as Styled from '../../../Common/Styles/portalManagement.styled';
import { useGetPermissions } from '../../../Hooks/Common/useGetPermissions';
import { useGetApprovedCdmsByDataOwner } from '../../../Hooks/DataOwner/useGetCDMs';
import { useGetDataOwner } from '../../../Hooks/DataOwner/useGetDataOwner';
import { useGetAllRequestsByDataOwner } from '../../../Hooks/DataOwner/useGetDataOwnerRequests';
import { useGetApprovedRuleSetsByDataOwner } from '../../../Hooks/DataOwner/useGetRuleSets';
import { AddDataOwnerRequestPanel } from './AddDataOwnerRequestPanel';
import { DataOwnerDetailsHeader, DataOwnerDetailsHeaderLoading } from './DataOwnerManagementHeader';
import {
  ALL_FILTER_KEY,
  displayValue,
  filterRuleSets,
  getDataSourceEntityName,
  getEnvironmentLabel,
  getRuleCount,
  getRuleSetCountForCdm,
  sortRuleSets,
} from './RuleSetHelpers';
import { ViewDataOwnerRequestPanel } from './ViewDataOwnerRequestPanel';
import { ViewRuleSetPanel } from './ViewRuleSetPanel';

type DataOwnerManagementPageProps = {
  addNew: boolean;
};

export function DataOwnerManagementPage(props: DataOwnerManagementPageProps): React.ReactElement {
  const { addNew } = props;
  const { paramDataOwnerId, paramRequestId }: never = useParams();
  const { httpClient } = useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const user = useUser();

  // DataOwner Requests State
  const [addEditDataOwnerRequestPanelOpen, setAddEditDataOwnerRequestPanelOpen] = React.useState<boolean>(addNew);
  const [viewDataOwnerRequestPanelOpen, setViewDataOwnerRequestPanelOpen] = React.useState<boolean>(false);

  // Remove DataOwner Request State
  const [removeRequestButtonDisabled, setRemoveRequestButtonDisabled] = React.useState<boolean>(true);

  // Popup from submitting DataOwner Request State
  const [showConfirmationPopup, setShowConfirmationPopup] = React.useState<boolean>(false);
  const [isSaveDataOwnerRequest, setIsSaveDataOwnerRequest] = React.useState<boolean>(false);
  const [isSubmitDataOwnerRequest, setIsSubmitDataOwnerRequest] = React.useState<boolean>(false);
  const [isRequestDeleted, setIsRequestDeleted] = React.useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);
  const [onSubmitErrorFlag, setOnSubmitErrorFlag] = useState<boolean>(false);

  // DataOwner Requests Table State
  const [selectedPivotKey, setSelectedPivotKey] = React.useState('Requests');
  const [selectedGridData, setSelectedGridData] = React.useState<DataOwnerRequestRecord>(null);
  const [myPermissions, isMyPermissionsLoading] = useGetPermissions();

  const [isDataOwnerFullReqLoading, setIsDataOwnerFullReqLoading] = useState<boolean>(false);

  // Approved Rule Sets Table State
  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSetRecord>(null);
  const [ruleSetSearchText, setRuleSetSearchText] = useState<string>('');
  const [ruleSetEnvironmentFilter, setRuleSetEnvironmentFilter] = useState<string>(ALL_FILTER_KEY);
  const [ruleSetStatusFilter, setRuleSetStatusFilter] = useState<string>(ALL_FILTER_KEY);
  const [ruleSetEntityFilter, setRuleSetEntityFilter] = useState<string>(ALL_FILTER_KEY);
  const [ruleSetSortKey, setRuleSetSortKey] = useState<string>(undefined);
  const [isRuleSetSortDescending, setIsRuleSetSortDescending] = useState<boolean>(false);

  const shouldReload = showSuccessMessage && addEditDataOwnerRequestPanelOpen == false;

  const [dataOwnerRecord, isDataOwnerRecordLoading] = useGetDataOwner(paramDataOwnerId);
  const [dataOwnerRequestsList, isDataOwnerRequestsListLoading] = useGetAllRequestsByDataOwner(
    paramDataOwnerId,
    shouldReload
  );

  //To-Do: Make these calls as one call to Back-end & filter list in the front-end
  const [nonProdApprovedCdmList, isNonProdApprovedCdmListLoading] = useGetApprovedCdmsByDataOwner(paramDataOwnerId, 0);
  const [prodApprovedCdmList, isProdApprovedCdmListLoading] = useGetApprovedCdmsByDataOwner(paramDataOwnerId, 1);
  const [nonProdApprovedRuleSetList, isNonProdApprovedRuleSetListLoading] = useGetApprovedRuleSetsByDataOwner(
    paramDataOwnerId,
    0,
    shouldReload
  );
  const [prodApprovedRuleSetList, isProdApprovedRuleSetListLoading] = useGetApprovedRuleSetsByDataOwner(
    paramDataOwnerId,
    1,
    shouldReload
  );

  // Route Param: AddNew
  React.useEffect(() => {
    if (!isDataOwnerRecordLoading && addNew) {
      setAddEditDataOwnerRequestPanelOpen(true);
    }
  }, [isDataOwnerRecordLoading]);

  // Route Param: paramRequestId
  React.useEffect(() => {
    if (!isDataOwnerRecordLoading && !isDataOwnerRequestsListLoading && paramRequestId) {
      const paramDataOwnerRequest = dataOwnerRequestsList.find((dataOwnerRequest) => {
        return dataOwnerRequest.id === paramRequestId
      });
      openRequestPanel(paramDataOwnerRequest, false);
    }
  }, [isDataOwnerRecordLoading, isDataOwnerRequestsListLoading]);

  // Update view for privacy role
  React.useEffect(() => {
    if (myPermissions.isPrivacyReviewer && selectedPivotKey != 'PendingPrivacy') {
      setSelectedPivotKey('PendingPrivacy');
    }
  }, [isMyPermissionsLoading]);

  const dataOwnerRequestsTableColumns: IColumn[] = [
    {
      key: 'name',
      name: 'Name',
      minWidth: 200,
    },
    {
      key: 'submittedDate',
      name: 'Modified Date',
      minWidth: 150,
    },
    {
      key: 'environment',
      name: 'Environment',
      minWidth: 150,
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 150,
    },
  ];

  const dataOwnerCdmTableColumns: IColumn[] = [
    {
      key: 'name',
      name: 'Name',
      minWidth: 280
    },
    {
      key: 'isEO14117Protected',
      name: 'Is EO14117 Protected',
      minWidth: 140,
    },
    {
      key: 'ruleSetCount',
      name: 'Rule Set Count',
      minWidth: 110,
    },
    {
      key: 'environment',
      name: 'Environment',
      minWidth: 150,
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 150,
    },
  ];

  const onRuleSetColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    const isDescending = ruleSetSortKey === column.key ? !isRuleSetSortDescending : false;
    setRuleSetSortKey(column.key);
    setIsRuleSetSortDescending(isDescending);
  };

  const dataOwnerRuleSetsTableColumns: IColumn[] = [
    { key: 'dataSourceEntity', name: 'Entity/CDM', minWidth: 140, maxWidth: 240 },
    { key: 'name', name: 'Name', minWidth: 180, maxWidth: 320 },
    { key: 'category', name: 'Category', minWidth: 120, maxWidth: 200 },
    { key: 'version', name: 'Version', minWidth: 60, maxWidth: 80 },
    { key: 'ruleSetType', name: 'Type', minWidth: 80, maxWidth: 100 },
    { key: 'tags', name: 'Tags', minWidth: 120, maxWidth: 240 },
    { key: 'ruleCount', name: 'Rule Count', minWidth: 80, maxWidth: 100 },
    { key: 'environment', name: 'Environment', minWidth: 120, maxWidth: 150 },
    { key: 'status', name: 'Status', minWidth: 100, maxWidth: 150 },
  ].map((column) => ({
    ...column,
    isResizable: true,
    isMultiline: true,
    isSorted: ruleSetSortKey === column.key,
    isSortedDescending: ruleSetSortKey === column.key && isRuleSetSortDescending,
    sortAscendingAriaLabel: 'Sorted A to Z',
    sortDescendingAriaLabel: 'Sorted Z to A',
    onColumnClick: onRuleSetColumnClick,
  }));

  const allApprovedRuleSets: RuleSetRecord[] = (prodApprovedRuleSetList ?? []).concat(nonProdApprovedRuleSetList ?? []);
  const isApprovedRuleSetListLoading = isNonProdApprovedRuleSetListLoading || isProdApprovedRuleSetListLoading;
  const allApprovedCdms: CDMRecord[] = (prodApprovedCdmList ?? []).concat(nonProdApprovedCdmList ?? []);
  const displayedRuleSets: RuleSetRecord[] = sortRuleSets(
    filterRuleSets(allApprovedRuleSets, {
      searchText: ruleSetSearchText,
      environment: ruleSetEnvironmentFilter,
      status: ruleSetStatusFilter,
      entityTypeId: ruleSetEntityFilter,
    }),
    ruleSetSortKey,
    isRuleSetSortDescending,
    allApprovedCdms
  );

  const ruleSetEnvironmentOptions: IDropdownOption[] = [
    { key: ALL_FILTER_KEY, text: 'All environments' },
    { key: '1', text: 'Production' },
    { key: '0', text: 'Non-Production' },
  ];

  const ruleSetStatusOptions: IDropdownOption[] = [
    { key: ALL_FILTER_KEY, text: 'All statuses' },
    ...Array.from(new Set(allApprovedRuleSets.map((ruleSet) => ruleSet.status).filter((status) => !!status)))
      .sort()
      .map((status) => ({ key: status, text: status })),
  ];

  const ruleSetEntityOptions: IDropdownOption[] = [
    { key: ALL_FILTER_KEY, text: 'All entities/CDMs' },
    ...Array.from(
      new Map(
        allApprovedRuleSets
          .filter((ruleSet) => !!ruleSet?.dataSource?.entityTypeId)
          .map((ruleSet): [string, string] => [
            ruleSet.dataSource.entityTypeId,
            getDataSourceEntityName(ruleSet, allApprovedCdms),
          ])
      )
    )
      .map(([entityTypeId, entityName]) => ({ key: entityTypeId, text: entityName }))
      .sort((a, b) => a.text.localeCompare(b.text)),
  ];

  const onRenderRuleSetItemColumn = (item: RuleSetRecord, index: number, column: IColumn): React.ReactNode => {
    switch (column.key) {
      case 'name':
        return (
          <Link onClick={() => setSelectedRuleSet(item)} aria-label={`View details for rule set ${item.name}`}>
            {displayValue(item.name)}
          </Link>
        );
      case 'category':
        return <Text>{displayValue(item.category)}</Text>;
      case 'version':
        return <Text>{displayValue(item.version)}</Text>;
      case 'ruleSetType':
        return <Text>{displayValue(item.ruleSetType)}</Text>;
      case 'tags':
        return <Text>{displayValue(item.tags)}</Text>;
      case 'ruleCount':
        return <Text>{getRuleCount(item)}</Text>;
      case 'environment':
        return <Text>{getEnvironmentLabel(item.environment)}</Text>;
      case 'dataSourceEntity':
        return <Text>{getDataSourceEntityName(item, allApprovedCdms)}</Text>;
      default:
        return onRenderItemColumn(item, index, column);
    }
  };

  const onRenderItemColumn = (item: any, index: number, column: IColumn): React.ReactNode => {
    if (column.key === 'submittedDate') {
      return <Text>{new Date(item.submittedDate).toLocaleDateString()}</Text>;
    } else if (column.key === 'environment' && item.environment == 0) {
      return <Text>Non-Production</Text>;
    } else if (column.key === 'environment' && item.environment == 1) {
      return <Text>Production</Text>;
    } else if (column.key === 'status' && item.status === 'Draft') {
      return <Styled.DraftStatus>Draft</Styled.DraftStatus>;
    } else if (column.key === 'status' && item.status === 'Submitted') {
      return <Styled.SubmittedStatus>Submitted</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'PendingPrivacy') {
      return <Styled.SubmittedStatus>Pending Privacy</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'PendingApproval') {
      return <Styled.SubmittedStatus>Pending Approval</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'Rejected') {
      return <Styled.RejectedStatus>Rejected</Styled.RejectedStatus>;
    } else if (column.key === 'status' && item.status === 'Approved') {
      return <Styled.ApprovedStatus>Approved</Styled.ApprovedStatus>;
    } else if (column.key === 'status' && item.status === 'Provisioned') {
      return <Styled.ProvisionedStatus>Provisioned</Styled.ProvisionedStatus>;
    } else if (column.key === 'name' && (item.status === 'Draft' || item.status === 'Rejected')) {
      return (
        <Styled.DataOwnerName onClick={async () => await openRequestPanel(item, true)}>
          {item.name}
        </Styled.DataOwnerName>
      );
    } else if (column.key == 'name' && item.status != null && item.privacyApprovalArtifacts != null) {
      return (
        <Styled.DataOwnerName onClick={async () => await openRequestPanel(item, false)}>
          {item.name}
        </Styled.DataOwnerName>
      );
    } else if (column.key == 'name') {
      return <Text>{item.name}</Text>;
    } else if (column.key == 'id') {
      return <Text>{item.id}</Text>;
    } else if (column.key === 'type') {
      const typeKey = String(item.type ?? '');

      const cdmType =
        typeKey === String(Constants.CDM.cdmType1OptionKey)
          ? 'Full Journal'
          : typeKey === String(Constants.CDM.cdmType2OptionKey)
          ? 'Current-Dated Data'
          : '\u2014';

      return <Text>{cdmType}</Text>;
    } else if (column.key === 'ruleSetCount') {
      if (isApprovedRuleSetListLoading) {
        return <Text>{'\u2026'}</Text>;
      }
      return <Text>{getRuleSetCountForCdm(allApprovedRuleSets, item.id, item.environment)}</Text>;
    } else if (column.key === 'isEO14117Protected') {
      const isProtected = item.isEO14117Protected || false;
      return (
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Text>{isProtected ? 'Yes' : 'No'}</Text>
          {isProtected && (
            <TooltipHost content="This CDM is protected under Executive Order 14117">
              <Styled.EoProtectedIcon iconName="Lock" aria-label="EO14117 Protected" />
            </TooltipHost>
          )}
        </Stack>
      );
    }
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addRequest',
      text: 'Add Request',
      iconProps: { iconName: 'Add' },
      disabled:
        (isDataOwnerRecordLoading &&
          isMyPermissionsLoading &&
          isNonProdApprovedCdmListLoading &&
          isProdApprovedCdmListLoading &&
          isNonProdApprovedRuleSetListLoading &&
          isProdApprovedRuleSetListLoading) ||
        myPermissions.isPrivacyReviewer,
      onClick: () => {
        setAddEditDataOwnerRequestPanelOpen(true);
      },
    },
    {
      key: 'removeRequest',
      text: 'Remove Request',
      iconProps: { iconName: 'Delete' },
      disabled: removeRequestButtonDisabled,
      onClick: () => {
        handleRemoveRequestButton();
      },
    },
    ...(selectedPivotKey === 'RuleSets' && !myPermissions.isPrivacyReviewer
      ? [
          {
            key: 'addRuleRequest',
            text: 'Add Rule Request',
            iconProps: { iconName: 'AddNotes' },
            disabled:
              isDataOwnerRecordLoading ||
              isMyPermissionsLoading ||
              isNonProdApprovedCdmListLoading ||
              isProdApprovedCdmListLoading ||
              isApprovedRuleSetListLoading,
            onClick: () => {
              setAddEditDataOwnerRequestPanelOpen(true);
            },
          },
        ]
      : []),
  ];

  const handleRemoveRequestButton = () => {
    setShowConfirmationPopup(true);
    httpClient
      .delete(`${__API_BASE_URL__}/tenant/deleteRequest?requestId=${selectedGridData.id}`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
      })
      .then(() => {
        setShowSuccessMessage(true);
        setIsRequestDeleted(true);
      })
      .catch(() => {
        setShowErrorMessage(true);
      });
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  const onRenderCheckbox = (props: IDetailsListCheckboxProps | undefined) => {
    return <CoherenceCheck {...props} />;
  };

  const initialDataOwnerRequestDetails: IDataOwnerRequestContext = {
    dataOwnerRequestDetails:{
      dataOwnerId: dataOwnerRecord?.id,
      dataOwnerName: dataOwnerRecord?.name,
      attachmentList: [],
      deleteAttachmentRequestList: [],
      nonProdApprovedCdms: [],
      prodApprovedCdms: [],
      nonProdApprovedRuleSets: [],
      prodApprovedRuleSets: [],
      savedCdmIdList: [],
      cdmDeletionList: [],
      savedRuleSetIdList: [],
      ruleSetDeletionList: [],
      request: {
        id: uuidv4(),
        name: '',
        description: '',
        tenantId: paramDataOwnerId,
        requestorUpn: user?.email,
        environment: 0,
        status: 'Draft',
        submittedDate: new Date().toISOString(),
        privacyApprovalArtifacts: [],
        privacyApprovalDate: new Date().toISOString(),
        additionalNotes: '',
      },
      cdms: [],
      ruleSets: [],
      addUpdateRequest: (value: DataOwnerRequestRecord) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.request = value;
        });
      },
      addCdmList: (value: CDMRecord) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.cdms?.push(value);
        });
      },
      updateCdmList: (key: number, value: CDMRecord) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.cdms[key] = value;
        });
      },
      deleteCdm: (idList: string[]) => {
        updateDataOwnerRequestDetails((draft) => {
          const newCdms: CDMRecord[] = draft.cdms.filter((cdmInList) => {
            return idList.includes(cdmInList.id) == false;
          });
          draft.cdms = newCdms;
        });
      },
      updateDeleteCdmList: (value: string[]) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.cdmDeletionList = value;
        });
      },
      addRuleSetList: (value: RuleSetRecord) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.ruleSets.push(value);
        });
      },
      updateRuleSetInList: (key: number, value: RuleSetRecord) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.ruleSets[key] = value;
        });
      },
      deleteRuleSets: (idList: string[]) => {
        updateDataOwnerRequestDetails((draft) => {
          const newRuleSetsList: RuleSetRecord[] = draft.ruleSets.filter((ruleSetInList) => {
            return idList.includes(ruleSetInList.id) == false;
          });
          draft.ruleSets = newRuleSetsList;
        });
      },
      updateDeleteRuleSetList: (value: string[]) => {
        updateDataOwnerRequestDetails((draft) => {
          draft.ruleSetDeletionList = value;
        });
      },
      updateAttachmentList: (value: AttachmentItem[]) => {
        updateDataOwnerRequestDetails((p) => {
          p.attachmentList = value;
        });
      },
      updateDeleteAttachmentRequestList: (value: DeleteAttachmentRequest[]) => {
        updateDataOwnerRequestDetails((p) => {
          p.deleteAttachmentRequestList = value;
        });
      },
    }
  };

  // Set/Update state using Immer helps in mutating the draft state directly for ease of modifing nested objects/arrays
  const [dataOwnerRequestDetails, updateDataOwnerRequestDetails] =
    useImmer<IDataOwnerRequestDetails>(initialDataOwnerRequestDetails.dataOwnerRequestDetails);

  useEffect(() => {
    updateDataOwnerRequestDetails({
      ...dataOwnerRequestDetails,
      dataOwnerId: dataOwnerRecord?.id,
      dataOwnerName: dataOwnerRecord?.name,
    });
  }, [dataOwnerRecord]);

  useEffect(() => {
    if (!isNonProdApprovedCdmListLoading && !isProdApprovedCdmListLoading) {
      const newDataOwnerRequestDetails: IDataOwnerRequestDetails = {
        ...dataOwnerRequestDetails,
        nonProdApprovedCdms: nonProdApprovedCdmList,
        prodApprovedCdms: prodApprovedCdmList,
      };
      updateDataOwnerRequestDetails(newDataOwnerRequestDetails);
    }
  }, [isNonProdApprovedCdmListLoading, isProdApprovedCdmListLoading]);

  useEffect(() => {
    if (!isNonProdApprovedRuleSetListLoading && !isProdApprovedRuleSetListLoading) {
      updateDataOwnerRequestDetails({
        ...dataOwnerRequestDetails,
        nonProdApprovedRuleSets: nonProdApprovedRuleSetList,
        prodApprovedRuleSets: prodApprovedRuleSetList,
      });
    }
  }, [isNonProdApprovedRuleSetListLoading, isProdApprovedRuleSetListLoading]);

  // SAVE Request Details
  useEffect(() => {
    if (isSaveDataOwnerRequest || isSubmitDataOwnerRequest) {
      // This endpoint will save the Privacy Attachments in Blob Storage along with Request upsert call.
      const requestToBeSaved = {
        ...dataOwnerRequestDetails,
        request: {
          ...dataOwnerRequestDetails.request,
          requestorUpn: user?.email,
          submittedDate: new Date().toISOString(),
        },
      };
      if (isSubmitDataOwnerRequest && !onSubmitErrorFlag) {
        requestToBeSaved.request.status = Constants.RequestStatus.SubmittedStatusValue;
      }

      new Promise<void>((resolve) => {
        httpClient
          .post(`${__API_BASE_URL__}/tenant/upsertFullRequestDetails`, {
            resource: __CLIENT_ID__,
            headers: {
              'Content-Type': 'application/json',
              'X-Content-Type-Options': 'nosniff',
            },
            data: requestToBeSaved,
          })
          .then(() => {
            if (isSubmitDataOwnerRequest && onSubmitErrorFlag) {
              setShowErrorMessage(true);
            } else {
              setShowSuccessMessage(true);
            }
          })
          .catch(() => {
            setShowErrorMessage(true);
          })
          .then(() => {
            setIsSaveDataOwnerRequest(false);
            resolve();
          });
      });
      setShowConfirmationPopup(true);
      dismissAddDataOwnerRequestPanel();
    }
  }, [isSaveDataOwnerRequest, isSubmitDataOwnerRequest]);

  const openRequestPanel = async (item: DataOwnerRequestRecord, isEditMode: boolean) => {
    if (!shouldReload) {
      const tempContextData = initialDataOwnerRequestDetails.dataOwnerRequestDetails;
      setIsDataOwnerFullReqLoading(true);
      tempContextData.nonProdApprovedCdms = nonProdApprovedCdmList;
      tempContextData.prodApprovedCdms = prodApprovedCdmList;
      tempContextData.nonProdApprovedRuleSets = nonProdApprovedRuleSetList;
      tempContextData.prodApprovedRuleSets = prodApprovedRuleSetList;
      new Promise<void>(() => {
        httpClient
          .get<IDataOwnerRequestDetails>(`${__API_BASE_URL__}/tenant/getFullRequestDetailsById?requestId=${item.id}`, {
            resource: __CLIENT_ID__,
            headers: {
              'Content-Type': 'application/json',
              'X-Content-Type-Options': 'nosniff',
            },
          })
          .then(({ data }) => {
            tempContextData.request = data.request;
            // Only editable requests get the Pull default; viewing a request must not alter it.
            tempContextData.cdms = isEditMode ? data.cdms.map(withDefaultIngestionMethod) : data.cdms;
            tempContextData.ruleSets = data.ruleSets;
            tempContextData.attachmentList = data.request.privacyApprovalArtifacts;
            tempContextData.savedCdmIdList = data.cdms.map((x) => {
              return x.id;
            });
            tempContextData.savedRuleSetIdList = data.ruleSets.map((x) => {
              return x.id;
            });
            updateDataOwnerRequestDetails(tempContextData);
            setIsDataOwnerFullReqLoading(false);
          });
      });
    }
    if (isEditMode) {
      setAddEditDataOwnerRequestPanelOpen(true);
      return;
    }
    setViewDataOwnerRequestPanelOpen(true);
  };

  const selection = new Selection({
    onSelectionChanged: () => {
      changeDataOwnerRequestSelection();
    },
  });

  const changeDataOwnerRequestSelection = () => {
    const selectionCount = selection.getSelectedCount();
    if (selectionCount == 0) {
      setRemoveRequestButtonDisabled(true);
    } else {
      const selectedDataOwnerRequestRecord: DataOwnerRequestRecord =
        selection.getSelection()[0] as DataOwnerRequestRecord;
      setSelectedGridData(selectedDataOwnerRequestRecord);
      if (selectedDataOwnerRequestRecord.status === 'Draft') {
        setRemoveRequestButtonDisabled(false);
      }
      if (selectedDataOwnerRequestRecord.status === 'Rejected') {
        setRemoveRequestButtonDisabled(false);
      }
    }
  };

  const handlePivotChange = (item?: PivotItem) => {
    if (item) {
      setSelectedPivotKey(item.props.itemKey);
    }
  };

  const dismissAddDataOwnerRequestPanel = () => {
    //reset context
    updateDataOwnerRequestDetails({
      ...initialDataOwnerRequestDetails.dataOwnerRequestDetails,
      nonProdApprovedCdms: nonProdApprovedCdmList,
      prodApprovedCdms: prodApprovedCdmList,
      nonProdApprovedRuleSets: nonProdApprovedRuleSetList,
      prodApprovedRuleSets: prodApprovedRuleSetList,
    });
    setAddEditDataOwnerRequestPanelOpen(false);
  };

  const dismissViewDataOwnerRequestPanel = () => {
    //reset context
    updateDataOwnerRequestDetails({
      ...initialDataOwnerRequestDetails.dataOwnerRequestDetails,
      nonProdApprovedCdms: nonProdApprovedCdmList,
      prodApprovedCdms: prodApprovedCdmList,
      nonProdApprovedRuleSets: nonProdApprovedRuleSetList,
      prodApprovedRuleSets: prodApprovedRuleSetList,
    });
    setViewDataOwnerRequestPanelOpen(false);
  };

  const closeConfirmationPopup = () => {
    setIsSaveDataOwnerRequest(false);
    setIsSubmitDataOwnerRequest(false);
    setIsRequestDeleted(false);
    setShowConfirmationPopup(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setOnSubmitErrorFlag(false);
  };

  return (
    <div data-testid="data-owner-management-page">
      {isDataOwnerRecordLoading ? (
        <DataOwnerDetailsHeaderLoading />
      ) : (
        <DataOwnerDetailsHeader dataOwnerRecord={dataOwnerRecord} />
      )}

      {!isDataOwnerRequestsListLoading && !isMyPermissionsLoading && myPermissions.isPrivacyReviewer && (
        <Styled.PortalManagementPivot
          aria-label="Data Owner Request Tables Pivot"
          selectedKey="PendingPrivacy"
          onLinkClick={handlePivotChange}
          defaultSelectedKey="PendingPrivacy"
        >
          <PivotItem
            headerText="Pending Privacy"
            itemKey="PendingPrivacy"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'PendingPrivacy',
            }}
          />
        </Styled.PortalManagementPivot>
      )}

      {!isDataOwnerRequestsListLoading && !isMyPermissionsLoading && !myPermissions.isPrivacyReviewer && (
        <Styled.PortalManagementPivot
          aria-label="Data Owner Request Tables Pivot"
          selectedKey={selectedPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="Requests"
        >
          <PivotItem
            headerText="Requests"
            itemKey="Requests"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'Requests',
            }}
          />
          <PivotItem
            headerText="Approved CDMs"
            itemKey="CDMs"
            headerButtonProps={{
              'data-order': 2,
              'data-title': 'CDMs',
            }}
          />
          <PivotItem
            headerText="Approved Rule Sets"
            itemKey="RuleSets"
            headerButtonProps={{
              'data-order': 3,
              'data-title': 'RuleSets',
            }}
          />
        </Styled.PortalManagementPivot>
      )}

      <Styled.ButtonsTableSection>
        <Styled.HorizontalScrollWrapper>
          <Styled.DataOwnerTableContentWrapper>
            <Styled.PortalManagementCommandBar items={commandBarItems} ariaLabel="Command Bar" />

            {selectedPivotKey == 'Requests' && (
              <MarqueeSelection selection={selection}>
                <ShimmeredDetailsList
                  items={dataOwnerRequestsList}
                  enableShimmer={isDataOwnerRequestsListLoading}
                  columns={dataOwnerRequestsTableColumns}
                  onRenderItemColumn={onRenderItemColumn}
                  onRenderRow={onRenderRow}
                  onRenderCheckbox={(props) => onRenderCheckbox(props)}
                  selection={selection}
                  selectionMode={SelectionMode.single}
                  layoutMode={DetailsListLayoutMode.fixedColumns}
                  constrainMode={ConstrainMode.unconstrained}
                  checkboxVisibility={CheckboxVisibility.always}
                  ariaLabelForShimmer="Data Owner Requests Table is being fetched"
                  ariaLabelForGrid="Data Owner Requests Table"
                  checkButtonAriaLabel="Select Data Owner Request"
                />
              </MarqueeSelection>
            )}

            {selectedPivotKey == 'CDMs' && (
              <ShimmeredDetailsList
                items={prodApprovedCdmList.concat(nonProdApprovedCdmList)}
                enableShimmer={isProdApprovedCdmListLoading || isNonProdApprovedCdmListLoading}
                columns={dataOwnerCdmTableColumns}
                onRenderItemColumn={onRenderItemColumn}
                onRenderRow={onRenderRow}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.unconstrained}
                checkboxVisibility={CheckboxVisibility.hidden}
                ariaLabelForShimmer="Data Owner CDM Table is being fetched"
                ariaLabelForGrid="Data Owner CDM Table"
              />
            )}

            {selectedPivotKey == 'RuleSets' && (
              <>
                <Stack
                  horizontal
                  wrap
                  tokens={{ childrenGap: 12 }}
                  verticalAlign="center"
                  styles={{ root: { paddingBottom: 12 } }}
                >
                  <Dropdown
                    ariaLabel="Filter by Entity/CDM"
                    selectedKey={ruleSetEntityFilter}
                    options={ruleSetEntityOptions}
                    onChange={(ev, option) => setRuleSetEntityFilter(String(option?.key ?? ALL_FILTER_KEY))}
                    styles={{ root: { width: 220 } }}
                  />
                  <SearchBox
                    placeholder="Search by name or category"
                    ariaLabel="Search approved rule sets by name or category"
                    value={ruleSetSearchText}
                    onChange={(ev, newValue) => setRuleSetSearchText(newValue ?? '')}
                    onClear={() => setRuleSetSearchText('')}
                    styles={{ root: { width: 280, height: 32 } }}
                  />
                  <Dropdown
                    ariaLabel="Filter by environment"
                    selectedKey={ruleSetEnvironmentFilter}
                    options={ruleSetEnvironmentOptions}
                    onChange={(ev, option) => setRuleSetEnvironmentFilter(String(option?.key ?? ALL_FILTER_KEY))}
                    styles={{ root: { width: 180 } }}
                  />
                  <Dropdown
                    ariaLabel="Filter by status"
                    selectedKey={ruleSetStatusFilter}
                    options={ruleSetStatusOptions}
                    onChange={(ev, option) => setRuleSetStatusFilter(String(option?.key ?? ALL_FILTER_KEY))}
                    styles={{ root: { width: 180 } }}
                  />
                </Stack>
                <ShimmeredDetailsList
                  items={displayedRuleSets}
                  enableShimmer={isApprovedRuleSetListLoading}
                  columns={dataOwnerRuleSetsTableColumns}
                  onRenderItemColumn={onRenderRuleSetItemColumn}
                  onRenderRow={onRenderRow}
                  selectionMode={SelectionMode.none}
                  layoutMode={DetailsListLayoutMode.fixedColumns}
                  constrainMode={ConstrainMode.unconstrained}
                  checkboxVisibility={CheckboxVisibility.hidden}
                  ariaLabelForShimmer="Data Owner Rule Sets Table is being fetched"
                  ariaLabelForGrid="Data Owner Rule Sets Table"
                />
                {!isApprovedRuleSetListLoading && allApprovedRuleSets.length > 0 && displayedRuleSets.length === 0 && (
                  <Text>No rule sets match the current search or filters.</Text>
                )}
              </>
            )}

            {selectedPivotKey == 'PendingPrivacy' && (
              <ShimmeredDetailsList
                items={dataOwnerRequestsList.filter((dataOwnerRequest: DataOwnerRequestRecord) => {
                  return dataOwnerRequest.status == selectedPivotKey;
                })}
                enableShimmer={isDataOwnerRequestsListLoading}
                columns={dataOwnerRequestsTableColumns}
                onRenderItemColumn={onRenderItemColumn}
                onRenderRow={onRenderRow}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.unconstrained}
                checkboxVisibility={CheckboxVisibility.hidden}
                ariaLabelForShimmer="Data Owner Request Table is being fetched"
                ariaLabelForGrid="Data Owner Request Table"
              />
            )}
          </Styled.DataOwnerTableContentWrapper>
        </Styled.HorizontalScrollWrapper>
      </Styled.ButtonsTableSection>

      <DataOwnerRequestContext.Provider value={{dataOwnerRequestDetails:dataOwnerRequestDetails,dataOwnerRequestsList: dataOwnerRequestsList}}>
        {addEditDataOwnerRequestPanelOpen && (
          <AddDataOwnerRequestPanel
            isOpen={addEditDataOwnerRequestPanelOpen}
            dismissPanel={dismissAddDataOwnerRequestPanel}
            setIsSaveDataOwnerRequest={setIsSaveDataOwnerRequest}
            setIsSubmitDataOwnerRequest={setIsSubmitDataOwnerRequest}
            setShowSuccessMessage={setShowSuccessMessage}
            setShowErrorMessage={setShowErrorMessage}
            onSubmitErrorFlg={onSubmitErrorFlag}
            setOnSubmitErrorFlg={setOnSubmitErrorFlag}
          />
        )}
        {viewDataOwnerRequestPanelOpen && (
          <ViewDataOwnerRequestPanel
            isOpen={viewDataOwnerRequestPanelOpen}
            dismissPanel={dismissViewDataOwnerRequestPanel}
          />
        )}
      </DataOwnerRequestContext.Provider>

      {selectedRuleSet && (
        <ViewRuleSetPanel
          isOpen={!!selectedRuleSet}
          ruleSet={selectedRuleSet}
          cdmList={prodApprovedCdmList.concat(nonProdApprovedCdmList)}
          dismissPanel={() => setSelectedRuleSet(null)}
        />
      )}

      <Dialog
        hidden={!showConfirmationPopup}
        onDismiss={closeConfirmationPopup}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        modalProps={{
          isBlocking: showSuccessMessage || showErrorMessage ? false : true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Stack>
          <Styled.DialogTitleLabel>
            {!showSuccessMessage &&
              !showErrorMessage &&
              (!isSubmitDataOwnerRequest ? PopupText.saveRequestInProgress : PopupText.submitRequestInProgress)}
            {showSuccessMessage &&
              (isRequestDeleted
                ? PopupText.requestDeleted
                : isSubmitDataOwnerRequest
                ? PopupText.submitRequestSuccess
                : PopupText.saveRequestSuccess)}
            {showErrorMessage && PopupText.userError}
          </Styled.DialogTitleLabel>
          {!showSuccessMessage && !showErrorMessage && <ProgressIndicator></ProgressIndicator>}
          <Styled.DialogBodyText>
            {!showSuccessMessage && !showErrorMessage && ''}
            {showSuccessMessage &&
              (isRequestDeleted
                ? PopupText.requestDeletedDescription
                : isSubmitDataOwnerRequest
                ? PopupText.requestSuccessDescription
                : PopupText.requestSavedDescription)}
            {showErrorMessage &&
              (onSubmitErrorFlag && isSubmitDataOwnerRequest
                ? PopupText.validationErrorOnSubmissionMsg
                : PopupText.userErrorDescription)}
          </Styled.DialogBodyText>
        </Stack>
      </Dialog>
    </div>
  );
}
